import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GavelIcon from '@mui/icons-material/Gavel';
import { useTranslation } from 'react-i18next';
import ContractService from '../../services/ContractService';
import { useToast } from '../../contexts/ToastContext';

const STAGE_TIMINGS = [0, 15000, 45000, 120000];   // ms offsets for the progress captions

/**
 * Collects a briefing (optionally AI-drafted from the quote) and generates the
 * contract body with the LLM. Serves both creation from a quote (mode="generate")
 * and regeneration of an editable contract (mode="regenerate").
 */
export default function GenerateContractModal({ open, onClose, quoteId, mode = 'generate', contractId, initialBrief, onDone }) {
    const { t, i18n } = useTranslation();
    const { showToast } = useToast();

    const [brief, setBrief] = useState('');
    const [drafting, setDrafting] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [stage, setStage] = useState(0);
    const stageTimers = useRef([]);

    const isRegenerate = mode === 'regenerate';

    useEffect(() => {
        if (open) setBrief(initialBrief || '');
    }, [open, initialBrief]);

    useEffect(() => () => stageTimers.current.forEach(clearTimeout), []);

    const handleDraft = async () => {
        setDrafting(true);
        try {
            const res = await ContractService.draftBrief(quoteId, i18n.language);
            setBrief(res.brief || '');
        } catch (err) {
            console.error('Failed to draft brief', err);
            showToast(t('contracts.generateModal.draftError'), 'error');
        } finally {
            setDrafting(false);
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        setStage(0);
        stageTimers.current = STAGE_TIMINGS.slice(1).map((delay, i) =>
            setTimeout(() => setStage(i + 1), delay)
        );
        try {
            const payload = { brief: brief.trim() || null, language: i18n.language };
            const contract = isRegenerate
                ? await ContractService.regenerate(contractId, payload)
                : await ContractService.generateFromQuote(quoteId, payload);

            if (contract.aiFallback) {
                showToast(t('contracts.generateModal.aiFallbackNotice'), 'warning');
            }
            onDone(contract);
        } catch (err) {
            console.error('Contract generation failed', err);
            showToast(t('contracts.generateModal.generateError'), 'error');
        } finally {
            stageTimers.current.forEach(clearTimeout);
            setGenerating(false);
        }
    };

    const handleClose = () => {
        if (generating) return;   // don't abandon a request in flight
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
        >
            <Box sx={{ height: 4, backgroundColor: '#427898' }} />
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 0.5 }}>
                <GavelIcon sx={{ color: '#427898', fontSize: 22 }} />
                <Typography component="span" fontWeight={700} sx={{ color: '#1a2332', fontSize: 17 }}>
                    {isRegenerate ? t('contracts.generateModal.regenerateTitle') : t('contracts.generateModal.title')}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                    {t('contracts.generateModal.subtitle')}
                </Typography>

                {isRegenerate && !generating && (
                    <Alert severity="warning" sx={{ borderRadius: 1.5, mb: 2 }}>
                        {t('contracts.generateModal.overwriteWarning')}
                    </Alert>
                )}

                {generating ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <LinearProgress sx={{ borderRadius: 2, mb: 2.5, '& .MuiLinearProgress-bar': { backgroundColor: '#427898' }, backgroundColor: '#eef4f8' }} />
                        <Typography variant="body2" sx={{ color: '#427898', fontWeight: 600 }}>
                            {t(`contracts.generateModal.stage${stage + 1}`)}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TextField
                            fullWidth
                            multiline
                            minRows={8}
                            maxRows={16}
                            label={t('contracts.generateModal.briefLabel')}
                            placeholder={t('contracts.generateModal.briefPlaceholder')}
                            helperText={t('contracts.generateModal.briefHelper')}
                            value={brief}
                            onChange={(e) => setBrief(e.target.value)}
                            disabled={drafting}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={drafting
                                ? <CircularProgress size={15} sx={{ color: '#427898' }} />
                                : <AutoAwesomeIcon sx={{ fontSize: '17px !important' }} />}
                            onClick={handleDraft}
                            disabled={drafting}
                            sx={{ mt: 1.5, borderColor: '#427898', color: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { borderColor: '#355f77', backgroundColor: '#F0F7FF' } }}
                        >
                            {drafting ? t('contracts.generateModal.drafting') : t('contracts.generateModal.draftWithAi')}
                        </Button>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleClose} disabled={generating} sx={{ color: '#64748b', textTransform: 'none' }}>
                    {t('contracts.generateModal.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleGenerate}
                    disabled={generating || drafting}
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: '17px !important' }} />}
                    sx={{ backgroundColor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: '8px', boxShadow: 'none', '&:hover': { backgroundColor: '#355f77', boxShadow: 'none' } }}
                >
                    {generating
                        ? t('contracts.generateModal.generating')
                        : isRegenerate ? t('contracts.generateModal.regenerate') : t('contracts.generateModal.generate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
